import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { AddToCartButton } from "../../../components/home/homePageComponents/ButtonComponents";
import { RentNowButton } from "./RentNowButton";
import RatingStars from "./RatingStars";
import config from "../../../config";

export default function BookOfTheMonth({ book }) {
  const navigate = useNavigate();
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [imageLoaded, setImageLoaded] = useState(false);

  if (!book) return null;

  const {
    bookId,
    bookImages = [],
    bookName,
    authorName,
    author,
    bookDescription,
    averageRating,
    bookIsbn10,
    bookStockStatus,
  } = book;

  console.log("Book of the Month Data:", book);

  // Modern crown icon
  const CrownIcon = () => (
    <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
      <path d="M12 2L8 7l-6 1 4 5-1 6 6-3 6 3-1-6 4-5-6-1-4-5z" />
    </svg>
  );

  return (
    <motion.section
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, ease: "easeOut" }}
      className="relative bg-white p-6 rounded-2xl shadow-lg max-w-6xl mx-auto border border-gray-100 my-6 overflow-hidden"
    >
      {/* Subtle gradient background */}
      <div className="absolute inset-0 bg-gradient-to-br from-orange-50/20 to-amber-50/10 pointer-events-none" />

      <div className="relative grid grid-cols-1 xl:grid-cols-12 gap-6 z-10">
        {/* Premium Badge - Compact Side Panel */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.2 }}
          className="xl:col-span-3 relative flex flex-col items-center text-center p-8 rounded-2xl overflow-hidden group"
        >
          {/* Background Image with Lighter Gradient Overlay */}
          <div
            className="absolute inset-0 bg-cover bg-center bg-no-repeat"
            style={{
              backgroundImage: 'url("/images/featured-pattern.jpg")', // Replace with your image path
            }}
          />
          {/* Lighter Gradient Overlay for highlighted background */}
          <div className="absolute inset-0 bg-gradient-to-br from-[#FF7236]/85 to-black" />
          <div className="absolute inset-0 bg-gradient-to-t from-white/15 via-transparent to-white/10" />

          {/* Animated Background Elements */}
          <motion.div
            animate={{
              scale: [1, 1.2, 1],
              opacity: [0.4, 0.6, 0.4],
            }}
            transition={{
              duration: 4,
              repeat: Infinity,
            }}
            className="absolute top-0 right-0 w-32 h-32 bg-white/20 rounded-full -translate-y-16 translate-x-16"
          />
          <motion.div
            animate={{
              scale: [1.2, 1, 1.2],
              opacity: [0.3, 0.5, 0.3],
            }}
            transition={{
              duration: 5,
              repeat: Infinity,
              delay: 1,
            }}
            className="absolute bottom-0 left-0 w-24 h-24 bg-amber-300/30 rounded-full translate-y-12 -translate-x-12"
          />

          <div className="relative z-10 w-full">
            {/* Enhanced Crown Badge */}
            <motion.div
              animate={{
                scale: [1, 1.05, 1],
                rotate: [0, -3, 3, 0],
                y: [0, -5, 0],
              }}
              transition={{
                duration: 4,
                repeat: Infinity,
                repeatDelay: 3,
              }}
              className="mb-6 relative mx-auto w-20 h-20"
            >
              {/* Outer Glow Effect */}
              <motion.div
                animate={{
                  scale: [1, 1.1, 1],
                  opacity: [0.5, 0.7, 0.5],
                }}
                transition={{
                  duration: 3,
                  repeat: Infinity,
                }}
                className="absolute inset-0 bg-white/40 rounded-2xl blur-sm"
              />

              {/* Main Badge */}
              <div className="relative w-20 h-20 bg-gradient-to-br from-white/40 to-white/20 rounded-2xl flex items-center justify-center backdrop-blur-md border border-white/40 shadow-lg">
                <CrownIcon />

                {/* Floating Particles */}
                <motion.div
                  animate={{
                    y: [0, -8, 0],
                    opacity: [0, 1, 0],
                  }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    delay: 0.5,
                  }}
                  className="absolute -top-1 -left-1 w-2 h-2 bg-yellow-200 rounded-full"
                />
                <motion.div
                  animate={{
                    y: [0, -6, 0],
                    opacity: [0, 1, 0],
                  }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    delay: 1,
                  }}
                  className="absolute -top-2 -right-2 w-1.5 h-1.5 bg-amber-100 rounded-full"
                />
              </div>

              {/* Number Badge */}
              <motion.div
                whileHover={{ scale: 1.1, rotate: 10 }}
                className="absolute -top-2 -right-2 w-7 h-7 bg-white rounded-full flex items-center justify-center shadow-lg border border-amber-100"
              >
                <span className="text-orange-500 text-xs font-black">1</span>
              </motion.div>
            </motion.div>

            {/* Enhanced Text Section */}
            <div className="space-y-4 mb-6">
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="space-y-3"
              >
                {/* Main Title */}
                <h2 className="text-2xl font-black leading-tight">
                  <span className="bg-gradient-to-r from-white to-amber-50 bg-clip-text text-transparent">
                    FEATURED
                  </span>
                  <br />
                  <span className="text-3xl font-black text-white drop-shadow-lg">
                    BOOK OF THE MONTH
                  </span>
                </h2>

                {/* Subtitle */}
                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.6 }}
                  className="text-amber-50 text-base font-semibold tracking-wide"
                >
                  ✦ Curated Excellence ✦
                </motion.p>
              </motion.div>
            </div>

            {/* Enhanced Rating Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8 }}
              className="w-full bg-white/15 rounded-xl p-4 backdrop-blur-lg border border-white/25 shadow-xl"
            >
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs font-bold text-white uppercase tracking-wider">
                  Rating
                </span>
                <div className="flex items-center gap-2">
                  <RatingStars rating={averageRating} size={14} />
                  <span className="text-sm font-black text-white bg-orange-400/40 px-2 py-1 rounded-lg">
                    {averageRating}
                  </span>
                </div>
              </div>

              {/* Enhanced Progress Bar */}
              <div className="h-2 bg-white/25 rounded-full overflow-hidden shadow-inner">
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: `${(averageRating / 5) * 100}%` }}
                  transition={{ delay: 1, duration: 1.2, ease: "easeOut" }}
                  className="h-full bg-gradient-to-r from-amber-200 to-orange-300 relative"
                >
                  {/* Shine Effect */}
                  <motion.div
                    animate={{ x: [-100, 300] }}
                    transition={{
                      duration: 2,
                      repeat: Infinity,
                      repeatDelay: 3,
                    }}
                    className="absolute top-0 left-0 w-20 h-full bg-white/40 skew-x-12"
                  />
                </motion.div>
              </div>

              {/* Rating Label */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.5 }}
                className="flex justify-between items-center mt-2"
              >
                <span className="text-xs text-white/90 font-medium">
                  {averageRating >= 4
                    ? "Excellent"
                    : averageRating >= 3
                    ? "Good"
                    : "Average"}
                </span>
                <span className="text-xs text-white/80 font-medium">
                  {((averageRating / 5) * 100).toFixed(0)}%
                </span>
              </motion.div>
            </motion.div>

            {/* Decorative Elements */}
            <motion.div
              animate={{
                rotate: 360,
                scale: [1, 1.1, 1],
              }}
              transition={{
                rotate: { duration: 8, repeat: Infinity, ease: "linear" },
                scale: { duration: 2, repeat: Infinity },
              }}
              className="absolute top-4 left-4 w-2 h-2 bg-amber-200 rounded-full opacity-70"
            />
            <motion.div
              animate={{
                rotate: -360,
                scale: [1.1, 1, 1.1],
              }}
              transition={{
                rotate: { duration: 6, repeat: Infinity, ease: "linear" },
                scale: { duration: 3, repeat: Infinity },
              }}
              className="absolute bottom-4 right-4 w-1.5 h-1.5 bg-white rounded-full opacity-50"
            />
          </div>
        </motion.div>

        {/* Book Content */}
        <div className="xl:col-span-9 grid grid-cols-1 lg:grid-cols-5 gap-6">
       {/* Image Gallery */}
<div className="lg:col-span-2 flex flex-col gap-2 h-full">
  {/* Main Image */}
  <motion.div
    whileHover={{ scale: 1.01 }}
    onClick={() => navigate(`/book/${bookId}`)}
    className={`relative rounded-xl overflow-hidden shadow-md cursor-pointer border transition-all ${
      bookImages.length === 1
        // reduced min height from 420 -> 360, and add a max height to keep card compact
        ? "h-full min-h-[360px] max-h-[520px] bg-gray-50 border-gray-200"
        : // keep aspect for multi images but slightly reduce apparent height
          "aspect-[3/4] max-h-[480px] bg-gray-50 border-gray-200"
    }`}
  >
    <AnimatePresence mode="wait">
      <motion.img
        key={activeImageIndex}
        src={`${config.url}${bookImages[activeImageIndex]}`}
        alt={bookName}
        initial={{ opacity: 0, scale: 1.02 }}
        animate={{ opacity: imageLoaded ? 1 : 0, scale: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.22, ease: "easeOut" }}
        className="w-full h-full object-cover"
        onLoad={() => setImageLoaded(true)}
        onError={(e) => {
          e.currentTarget.src = "/images/book-placeholder.jpg";
          setImageLoaded(true);
        }}
      />
    </AnimatePresence>

    {/* Loading skeleton */}
    {!imageLoaded && <div className="absolute inset-0 bg-gray-200 animate-pulse" />}
  </motion.div>

  {/* Thumbnail Strip (shown only when there are multiple images) */}
  {bookImages.length > 1 && (
    <div className="flex items-center gap-2 overflow-x-auto py-1 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
      {bookImages.map((img, index) => (
        <motion.button
          key={index}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => {
            setActiveImageIndex(index);
            setImageLoaded(false);
          }}
          className={`flex-shrink-0 w-12 aspect-[2/3] rounded-lg overflow-hidden border transition-all duration-150 ${
            activeImageIndex === index ? "border-orange-500 shadow" : "border-gray-200 hover:border-gray-300"
          }`}
          aria-label={`Show image ${index + 1}`}
        >
          <img
            src={`${config.url}${img}`}
            alt={`${bookName} ${index + 1}`}
            className="w-full h-full object-cover"
            draggable={false}
          />
        </motion.button>
      ))}
    </div>
  )}
</div>


          {/* Book Details */}
          <div className="lg:col-span-3 flex flex-col justify-between space-y-4">
            <div className="space-y-4">
              {/* Header */}
              <div>
                <motion.h3
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                  className="text-2xl font-black text-gray-900 leading-tight mb-1 line-clamp-2"
                >
                  {bookName}
                </motion.h3>

                <motion.p
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 }}
                  className="text-base text-gray-600 font-medium"
                >
                  by {author.authorName}
                </motion.p>
              </div>

              {/* Description */}
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                className="bg-gray-50 rounded-lg p-3 border border-gray-100"
              >
                <p className="text-gray-700 leading-relaxed text-sm line-clamp-10">
                  {bookDescription || "No description available for this book."}
                </p>
              </motion.div>

              {/* Compact Metadata */}
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 }}
                className="grid grid-cols-2 gap-3"
              >
                <div className="bg-gray-50 rounded-lg p-3 border border-gray-100">
                  <div className="text-xs font-semibold text-gray-500 mb-1">
                    ISBN-10
                  </div>
                  <div className="text-sm font-bold text-gray-900 font-mono">
                    {bookIsbn10}
                  </div>
                </div>

                <div className="bg-gray-50 rounded-lg p-3 border border-gray-100">
                  <div className="text-xs font-semibold text-gray-500 mb-1">
                    STATUS
                  </div>
                  <div
                    className={`text-sm font-bold ${
                      bookStockStatus === "IN_STOCK"
                        ? "text-green-600"
                        : "text-red-500"
                    }`}
                  >
                    {bookStockStatus
                      ? bookStockStatus.replace("_", " ")
                      : "NOT AVAILABLE"}
                  </div>
                </div>
              </motion.div>
            </div>

            {/* Action Buttons */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7 }}
              className="flex flex-col sm:flex-row gap-2"
            >
              <RentNowButton
                book={{
                  id: bookId,
                  image: bookImages[0] || "",
                  title: bookName,
                  author: authorName,
                  isbn: bookIsbn10,
                  inStock: bookStockStatus,
                }}
                className="flex-1 py-3 bg-gradient-to-r from-orange-500 to-amber-600 text-white font-bold rounded-lg shadow-sm hover:shadow transition-all duration-200 hover:scale-[1.02] text-sm"
              />

              {/* Fixed AddToCartButton with explicit text styling */}
              <AddToCartButton
                book={{
                  id: bookId,
                  image: bookImages[0] || "",
                  title: bookName,
                  author: authorName,
                  isbn: bookIsbn10,
                }}
                className="flex-1 py-3 text-gray-700 !text-gray-700 bg-white border border-gray-300 font-bold rounded-lg hover:border-orange-300 transition-all duration-200 hover:scale-[1.02] shadow-sm text-sm hover:!text-gray-900"
                labelClassName="!text-inherit font-semibold"
              />
            </motion.div>
          </div>
        </div>
      </div>

      {/* Subtle decorative elements */}
      <motion.div
        animate={{
          rotate: 360,
        }}
        transition={{
          duration: 15,
          repeat: Infinity,
          ease: "linear",
        }}
        className="absolute top-3 right-3 w-1 h-1 bg-orange-400 rounded-full opacity-40"
      />
    </motion.section>
  );
}
